import React from "react";

/**
 * Global crash safety net.
 *
 * Before this existed, ANY uncaught render error (or a stale JS bundle loaded
 * from a browser/CDN cache after a redeploy) left the user staring at a fully
 * blank screen with no way to recover. This boundary:
 *   1. Catches render-time exceptions and shows a branded fallback instead of
 *      a blank page.
 *   2. Detects the "stale chunk / bundle failed to load" class of errors
 *      (common right after a production redeploy) and offers a hard reload
 *      that bypasses the cache.
 */
function isStaleBundleError(error) {
  const msg = String(error?.message || error || "");
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isStale: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isStale: isStaleBundleError(error) };
  }

  componentDidCatch(error, info) {
    // Surface to the console for diagnostics (kept minimal — no PII).
    // eslint-disable-next-line no-console
    console.error("[VTS ErrorBoundary]", error, info?.componentStack);

    // Crash telemetry: report the exact error so production crashes are
    // debuggable (fire-and-forget, never throws).
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error || "unknown").slice(0, 500),
          stack: String(error?.stack || "").slice(0, 2000),
          component_stack: String(info?.componentStack || "").slice(0, 2000),
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch {
      /* never let telemetry crash the crash handler */
    }

    // A stale bundle after a redeploy can't be fixed by re-rendering — the
    // only cure is to fetch the fresh index.html + chunks. Auto-reload ONCE
    // (guarded so we never loop) to self-heal the blank-screen-on-reload case.
    if (isStaleBundleError(error)) {
      try {
        const KEY = "vts:stale-reload";
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        /* sessionStorage unavailable — fall through to the manual button */
      }
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem("vts:stale-reload");
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  handleHome = () => {
    try {
      sessionStorage.removeItem("vts:stale-reload");
    } catch {
      /* ignore */
    }
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isStale } = this.state;
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0e1a",
          color: "#e8edf7",
          padding: "24px",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div
            style={{
              fontSize: 13,
              letterSpacing: "0.18em",
              color: "#5b7cff",
              fontWeight: 700,
              marginBottom: 14,
            }}
          >
            VTS
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 10px" }}>
            {isStale ? "A new version is available" : "Something went wrong"}
          </h1>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: "#9aa6c0",
              margin: "0 0 24px",
            }}
          >
            {isStale
              ? "The app was updated while this tab was open. Reload to get the latest version."
              : "The page hit an unexpected error. Reloading usually fixes it — your account and data are safe."}
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={{
                background: "#3b5bff",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "12px 22px",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              data-testid="error-boundary-home-btn"
              onClick={this.handleHome}
              style={{
                background: "transparent",
                color: "#9aa6c0",
                border: "1px solid #2a3350",
                borderRadius: 10,
                padding: "12px 22px",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
